import { cn } from '@/lib/cn';

interface ProgressBarProps {
  value: number;
  className?: string;
  tone?: 'brand' | 'ok' | 'warn' | 'err';
}

const tones = {
  brand: 'bg-brand-400',
  ok: 'bg-ok',
  warn: 'bg-warn',
  err: 'bg-err',
} as const;

/**
 * Width transitions target `width` (not `all`) and use our strong ease-out
 * curve. A 500ms fill on progress changes feels confident without being
 * sluggish — long enough to register, short enough to feel live.
 */
export function ProgressBar({ value, className, tone = 'brand' }: ProgressBarProps) {
  const clamped = Math.max(0, Math.min(100, Math.round(value)));
  return (
    <div
      role="progressbar"
      aria-valuemin={0}
      aria-valuemax={100}
      aria-valuenow={clamped}
      className={cn('h-2 w-full rounded-full bg-surface-3 overflow-hidden', className)}
    >
      <div
        className={cn(
          'h-full rounded-full transition-[width] duration-500 ease-out-soft',
          tones[tone],
        )}
        style={{ width: `${clamped}%` }}
      />
    </div>
  );
}
